import { useEffect, useState } from 'react'

// Services
import { useCreateProductRequest } from 'lib/services/api/product-request/createProductRequest'
import { useGeneratePresignedUrlMutation } from 'lib/services/api/upload-attachments'

// Types
import { ProductRequestFormValues } from './ProductRequestForm.types'
import { NewAsset, Option } from 'common/types'
import { MediaFile } from 'components/organisms/MediaUpload/MediaUpload.types'
import { UnitOfMeasure } from 'lib/services/api/product-request/types'

// Router
import { useLocation, useNavigate } from 'react-router-dom'

// Context
import { useUndocumentedAssetsContext } from 'lib/context/UndocumentedAssetsContext'

// Utils
import mediaFileToFile from 'lib/utils/MediaFileToFile'
import { useAssetType } from 'lib/context/AssetTypeContext/AssetTypeContext'
import { getAssetTypeCaps } from 'lib/utils/getAssetType'
import { enqueueSnackbar } from 'notistack'

// Other
import axios from 'axios'
import { useForm } from 'react-hook-form'
import { parseAssetIdentifiers } from 'lib/utils/ParseAssetIdentifiers/parseAssetIdentifiers'
import { useUser } from 'app/User'
import { ProductRequestFormProps } from '../ManualAssetEntry.type'
import { useCookies } from 'react-cookie'

const useProductRequestFormLogic = (props: ProductRequestFormProps) => {
  // Services
  const { mutateAsync: createRequest, isLoading: isCreatingRequest } =
    useCreateProductRequest()
  const { mutateAsync: generatePresignedUrl } =
    useGeneratePresignedUrlMutation()

  // State
  const { isNurse } = useUser()
  const { assetType } = useAssetType()
  const { scanDetails } = props

  const { setSelectedResult } = scanDetails || {}
  const [cookies, setCookie] = useCookies(['productRequestData'])
  const { addUndocumentedScan, resetAddMutation } =
    useUndocumentedAssetsContext()
  const location = useLocation()
  const navigate = useNavigate()

  // Handle both manual entry flow and direct scan flow
  const locationState = location.state as {
    selectedCompany?: Option
    catalogNumber?: string
    deviceDescription?: string
    deviceId?: string
    expirationDate?: string
    lotBatch?: string
    serialNumber?: string
    scannedCode?: string
  } | null

  const {
    selectedCompany,
    catalogNumber,
    deviceDescription,
    deviceId,
    expirationDate,
    lotBatch,
    serialNumber,
    scannedCode,
  } = locationState || {}
  const [attachments, setAttachments] = useState<MediaFile[]>([])
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false)
  const [showAttachmentsError, setShowAttachmentsError] = useState(false)

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ProductRequestFormValues>({})

  const hasDeviceCount =
    watch('unitOfMeasure') === UnitOfMeasure.BOX ||
    watch('unitOfMeasure') === UnitOfMeasure.CASE

  const unitOfMeasureOptions = [
    { id: UnitOfMeasure.EACH, name: 'Each' },
    { id: UnitOfMeasure.BOX, name: 'Box' },
    { id: UnitOfMeasure.CASE, name: 'Case' },
  ]

  // Handlers
  const onSubmit = async (data: ProductRequestFormValues) => {
    if (attachments.length === 0) {
      return
    }

    try {
      setIsUploadingAttachments(true)
      const deviceCount =
        data.unitOfMeasure === UnitOfMeasure.EACH
          ? undefined
          : Number(data.deviceCount)

      const uploadedAttachments = await Promise.all(
        attachments.map(async (attachment) => {
          const { preSignedLink, directory } = await generatePresignedUrl(null)
          const file = await mediaFileToFile(attachment)
          await uploadToPresignedUrl(preSignedLink, file)
          return directory
        })
      )

      createRequest({
        catalogNumber: data.catalogNumber,
        deviceDescription: data.deviceDescription,
        unitOfMeasure: data.unitOfMeasure || UnitOfMeasure.EACH,
        deviceId: data.deviceId || undefined,
        serialNumber: data.serialNumber || undefined,
        expirationDate: data.expirationDate,
        deviceCount,
        companyId: data.company?.id as number,
        attachmentsDirectories:
          uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
      })
        .then((response) => {
          if (response.isProductRequest) {
            const { isMultipackHardware } = parseAssetIdentifiers({
              deviceDescription: data.deviceDescription,
              deviceCount: data.deviceCount || 1,
              idType: undefined,
              assetType: getAssetTypeCaps(assetType),
            })

            const productRequestData = {
              catalogNumber: data.catalogNumber,
              deviceDescription: data.deviceDescription,
              unitOfMeasure: data.unitOfMeasure,
              deviceId: data.deviceId || undefined,
              serialNumber: data.serialNumber || undefined,
              expirationDate: data.expirationDate,
              deviceCount: data.deviceCount,
              selectedCompany: data.company,
            }

            setCookie('productRequestData', {
              ...productRequestData,
            })

            if (
              getAssetTypeCaps(assetType) === 'biological' ||
              (isNurse &&
                getAssetTypeCaps(assetType) === 'non-biological' &&
                isMultipackHardware)
            ) {
              try {
                const asset = {
                  deviceDescription: response.product
                    ?.deviceDescription as string,
                  deviceId: response.product.deviceId,
                  deviceCount: response.product.deviceCount,
                  expirationDate: data.expirationDate || null,
                  lotBatch: data.serialNumber || undefined,
                  manufacturingDate: undefined,
                  versionModelNumber: response.product.versionModelNumber,
                  serialNumber: data.serialNumber || undefined,
                  udi: undefined,
                  issuingAgency: response.product.issuingAgency || 'UNKNOWN',
                  secondaryDeviceIdType: undefined,
                  chargeable: response.product.chargeable,
                  companyId: response.product.companyId,
                  cost: response.product.cost,
                  id: response.product.id,
                  gmdnPTDefinition: response.product.gmdnPTDefinition,
                  sizeString: response.product.sizeString,
                  sizeText: response.product.sizeText,
                  secondaryDeviceId: undefined,
                  isUDI: false,
                  catalogNumber: response.product.catalogNumber,
                  company: response.product.company,
                } as NewAsset

                setSelectedResult?.(asset)
                navigate(`../result`)
              } catch (error) {
                enqueueSnackbar('Failed to set selected result.', {
                  variant: 'error',
                })
                console.error('Failed to set selected result:', error)
              }
            } else {
              addUndocumentedScan({
                variables: {
                  data: [
                    {
                      bidAssetId: response.product.id,
                      bidCompanyId: response.product.companyId,
                      catalogNumber: response.product.catalogNumber,
                      deviceDescription: response.product.deviceDescription,
                      deviceId: response.product.deviceId,
                      deviceCount: response.product.deviceCount,
                      count: 1,
                      pkgQuantity: 1,
                      expirationDate: data.expirationDate
                        ? new Date(data.expirationDate).toISOString()
                        : undefined,
                      lotBatch: undefined,
                      companyName: response.product.company?.name,
                      manufacturingDate: undefined,
                      versionModelNumber: response.product.versionModelNumber,
                      serialNumber: undefined,
                      udi: undefined,
                      assetType: getAssetTypeCaps(assetType),
                      issuingAgency: response.product.issuingAgency,
                      secondaryDeviceIdType: undefined,
                      gmdnPTDefinition: response.product.gmdnPTDefinition,
                      cost: response.product.cost,
                      chargeable: response.product.chargeable,
                      isManualAddition: true,
                      parentCompany:
                        response.product.company?.parentCompany?.name,
                      parentCompanyId:
                        response.product.company?.parentCompanyId,
                      isExplanted: false,
                      isProductInGUDID:
                        response.product?.isProductInGUDID ?? false,
                    },
                  ],
                },
              })
                .then(() => {
                  resetAddMutation()
                  navigate('..')
                })
                .catch((error) => {
                  enqueueSnackbar(
                    'Failed to add product to undocumented list.',
                    {
                      variant: 'error',
                    }
                  )
                  console.error('Failed to create request:', error)
                })
            }
          } else {
            // Show a modal that the product exists and theres no need to create a request
            // TODO: Implement this
          }
        })
        .catch((error) => {
          enqueueSnackbar('Failed to create request.', {
            variant: 'error',
          })
          console.error('Failed to create request:', error)
        })
    } catch (error) {
      enqueueSnackbar('Failed to process request.', {
        variant: 'error',
      })
      console.error('Failed to process request:', error)
    } finally {
      setIsUploadingAttachments(false)
    }
  }

  const uploadToPresignedUrl = async (
    presignedUrl: string,
    selectedFile: File
  ) => {
    try {
      const uploadResponse = await axios.put(presignedUrl, selectedFile, {
        headers: {
          'Content-Type': 'image/jpeg',
        },
      })
      return uploadResponse
    } catch (error) {
      enqueueSnackbar('Failed to upload file.', {
        variant: 'error',
      })
      throw error
    }
  }

  useEffect(() => {
    if (cookies.productRequestData) {
      setValue('company', cookies.productRequestData.selectedCompany)
      setValue(
        'deviceDescription',
        cookies.productRequestData.deviceDescription
      )
      setValue('catalogNumber', cookies.productRequestData?.catalogNumber)
      setValue('deviceId', cookies.productRequestData?.deviceId)
      setValue('expirationDate', cookies.productRequestData?.expirationDate)
      setValue('serialNumber', cookies.productRequestData?.serialNumber)
      setValue(
        'unitOfMeasure',
        cookies.productRequestData?.unitOfMeasure || UnitOfMeasure.EACH
      )
      setValue('deviceCount', cookies.productRequestData?.deviceCount)
    } else {
      // Set values from location state (manual entry or direct scan)
      if (selectedCompany) {
        setValue('company', selectedCompany)
      }
      setValue('deviceDescription', deviceDescription || '')
      setValue('catalogNumber', catalogNumber || '')
      setValue('deviceId', deviceId || '')
      setValue('expirationDate', expirationDate || undefined)
      setValue('serialNumber', serialNumber || lotBatch || '')
    }
  }, [
    selectedCompany,
    deviceDescription,
    catalogNumber,
    deviceId,
    expirationDate,
    lotBatch,
    serialNumber,
    scannedCode,
    setValue,
    cookies,
  ])

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (attachments.length === 0) {
      setShowAttachmentsError(true)
    }

    handleSubmit(onSubmit)()
  }

  useEffect(() => {
    if (showAttachmentsError) {
      if (attachments.length > 0) {
        setShowAttachmentsError(false)
      }
    }
  }, [attachments, showAttachmentsError])

  return {
    errors,
    control,
    hasDeviceCount,
    isCreatingRequest,
    showAttachmentsError,
    isUploadingAttachments,
    unitOfMeasureOptions,
    navigate,
    handleOnSubmit,
    setAttachments,
  }
}

export default useProductRequestFormLogic
